import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaCheckCircle, FaPlus } from "react-icons/fa";
import { FaPencilAlt, FaExclamationTriangle, FaTrashAlt } from "react-icons/fa";
import { Button } from "../../components/ui/button";
import Modal from "../../components/Modal";
import { DataTable } from "../../components/DataTable";
import {
  groupService,
  groupDataMap,
  dashboardService,
  communityService,
} from "../../services";
import { Skeleton } from "../../components/ui/skeleton";
import useUserStore from "../../stores/userStore";
import { GrGroup } from "react-icons/gr";
import TableCard from "../../components/TableCard";
import { GoLog } from "react-icons/go";
import ShortenedText from "../../components/ShortenedText";
import LoadingImage from "../../components/LoadingImage";
import { createItemStore } from "../../stores/createItemStore";
import { toast } from "react-toastify";
import useUserStore2 from "../../stores/userStore2";
import StatusBadge from "../../components/table/StatusBadge"

// ========================================================
// Step 1: Zustand store instantiation
// ========================================================
const storeName = "groups";
const useItems = createItemStore(
  storeName,
  (group) => group.groupId,
  (args) => groupService.getGroups(args[0], args[1]),
  groupDataMap
);
// ========================================================

const GroupManagement = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const navigate = useNavigate();
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [groupToActivate, setGroupToActivate] = useState(null);
  const [loading, setLoading] = useState(false);
  const { userRole, userId } = useUserStore();
  const { communityId } = useParams();
  const [pendingRequests, setPendingRequests] = useState({
    total: 0,
    oldTotal: 0,
  });
  const { user } = useUserStore2();
  const location = useLocation();
  const [communityName, setCommunityName] = useState("");

  const [isPendingRequestsLoading, setIsPendingRequestsLoading] =
    useState(true);

  const columns = [
    userRole === "A"
      ? {
          accessorKey: "communityName",
          header: "Community Name",
          cell: ({ row }) => <span>{row.getValue("communityName")}</span>,
        }
      : {
          accessorKey: "groupId",
          header: "Group ID",
          cell: ({ row }) => (
            <span>
              <ShortenedText text={row.getValue("groupId")} />
            </span>
          ),
        },
    {
      accessorKey: "groupName",
      header: "Group Name",
      cell: ({ row }) => <span>{row.getValue("groupName")}</span>,
    },
    {
      accessorKey: "groupLeader",
      header: "Group Leader",
      cell: ({ row }) => <span>{row.getValue("groupLeader")}</span>,
    },
    {
      accessorKey: "totalMembers",
      header: "Total Members",
      cell: ({ row }) => <span>{row.getValue("totalMembers")}</span>,
    },
    {
      accessorKey: "dateCreated",
      header: "Date Created",
      cell: ({ row }) => <span>{row.getValue("dateCreated")}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status");
        return (
          <StatusBadge
            status={status === "Deactivated" && !row.original.verified
              ? "Invitation sent"
              : status}
          />
        );
      },
    },
  ];

  const handleConfirmActivate = async () => {
    if (!groupToActivate) return;
    setLoading(true);

    try {
      const payload = {
        EntityId: userId, // assuming userId is available in the scope
        GroupId: groupToActivate, // assuming groupToDelete is just the group ID
        Status: "A",
      };

      await groupService.editGroup(payload);
      fetchItems({}, [null, groupToActivate]);

      toast.success("🎉 Group has been activated successfully!", {
        autoClose: 2000,
      });
      setReload((prev) => !prev); // trigger table refresh
    } catch (error) {
      console.error("Activation failed:", error);
      toast.error("❌ Failed to activate group", {
        autoClose: 2000,
      });
    } finally {
      setShowActivateModal(false);
      setLoading(false);
      setGroupToActivate(null);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!groupToDelete) return;
    setLoading(true);

    try {
      const payload = {
        EntityId: userId, // assuming userId is available in the scope
        GroupId: groupToDelete, // assuming groupToDelete is just the group ID
        Status: "D",
      };

      await groupService.editGroup(payload);
      fetchItems({}, [null, groupToDelete]);

      toast.success("🎉 Group has been deactivated successfully!", {
        autoClose: 2000,
      });
      setReload((prev) => !prev); // trigger table refresh
    } catch (error) {
      console.error("Deactivation failed:", error);
      toast.error("❌ Failed to deactivate group", {
        autoClose: 2000,
      });
    } finally {
      setShowDeleteModal(false);
      setLoading(false);
      setGroupToDelete(null);
    }
  };

  const actions = (row) => [
    {
      label: "View/Edit Details",
      icon: <FaPencilAlt />,
      onClick: () => {
        navigate(`/group-management/edit/${row.groupId}`);
      },
      variant: "dropPlain",
    },
    {
      label: "Activate Group",
      icon: <FaCheckCircle />,
      onClick: () => {
        setGroupToActivate(row.groupId);
        setShowActivateModal(true);
      },
      variant: "dropGreen",
    },
    {
      label: "Deactivate Group",
      icon: <FaTrashAlt />,
      onClick: () => {
        setGroupToDelete(row.groupId);
        setShowDeleteModal(true);
      },
      variant: "dropRed",
    },
  ];

  const [reload, setReload] = useState(false);

  // ========================================================
  // Step 2: Zustand store and page hydration
  // ========================================================
  const { isLoading, fetchItems, getSortedFiltered } = useItems(
    (state) => state
  );
  console.log("communityId from params: ", communityId);
  const data =
    userRole === "A" // Get all groups for admin, filter by community for others
      ? getSortedFiltered()
      : getSortedFiltered((group) => group.communityId === communityId);
  console.log("data using commId: ", data);

  const showLoading = data.length === 0 && isLoading;
  // console.log(`${storeName}:`, data);
  useEffect(() => {
    fetchItems(); // Fetch all groups
  }, []);
  // ========================================================

  // Update the pending requests useEffect
  useEffect(() => {
    const fetchPendingRequests = async () => {
      if (!user?.id) {
        setIsPendingRequestsLoading(true);
        return;
      }

      try {
        setIsPendingRequestsLoading(true);
        const response = await dashboardService.countTotalPendingRequests(
          user.id
        );
        setPendingRequests({
          total: response.total_pending_requests,
          oldTotal: response.total_pending_requests_old,
        });
      } catch (error) {
        console.error("Error fetching pending requests:", error);
        // Optionally set some error state here
      } finally {
        setIsPendingRequestsLoading(false);
      }
    };

    fetchPendingRequests();
  }, [reload, user?.id]);

  useEffect(() => {
    const fetchCommunityName = async () => {
      try {
        // Skip if we already have the name in location state
        if (location.state?.communityName) {
          setCommunityName(location.state.communityName);
          return;
        }

        const response = await communityService.getCommunityDetails(
          communityId
        );

        if (response?.Data?.length > 0) {
          const name = response.Data[0].name;
          setCommunityName(name);
          // Update navigation state while preserving any existing state
          navigate(location.pathname, {
            ...location,
            state: {
              ...location.state,
              communityName: name,
            },
            replace: true,
          });
          // console.log("Fetched community name:", name);
          // console.log("Current location state:", location.state);
        }
      } catch (error) {
        console.error("Error fetching community name:", error);
      }
    };

    if (communityId) {
      fetchCommunityName();
    }
  }, [communityId, navigate, location]);

  return (
    <div className="flex flex-col md:flex-row min-h-screen ">
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-4 sm:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Total Groups - With Skeleton */}
            {showLoading ? (
              // {!page.groupsTable.length ? (
              <Skeleton className="w-full h-32 rounded-xl" />
            ) : (
              <TableCard
                heading="Total Groups"
                number={data.length}
                // number={totalGroups}
                trend={{ percentage: "20%", isUp: true }}
                bgColor="from-indigo-50 to-indigo-500"
                icon={GrGroup}
                iconColor="text-indigo-500"
                borderColor="border-l-indigo-500"
              />
            )}

            {/* pending requests - With Skeleton */}
            {isPendingRequestsLoading || showLoading ? (
              <Skeleton className="w-full h-32 rounded-xl" />
            ) : (
              <TableCard
                heading="Pending Requests"
                number={pendingRequests.total}
                trend={{
                  percentage: `${Math.abs(
                    ((pendingRequests.total - pendingRequests.oldTotal) /
                      (pendingRequests.oldTotal || 1)) *
                      100
                  ).toFixed(0)}%`,
                  isUp: pendingRequests.total > pendingRequests.oldTotal,
                }}
                bgColor="from-orange-50 to-orange-300"
                icon={GoLog}
                iconColor="text-yellow-400"
                buttonText="View More"
                buttonLink="/pending-requests"
                borderColor="border-l-yellow-400"
              />
            )}
          </div>

          <div className="bg-white shadow rounded-lg p-4 md:max-w-[calc(100vw-375px)] sm:p-6 text-sm mt-6">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
              <div className="text-base sm:text-lg font-semibold text-[#111827] mb-4 sm:mb-0">
                Community Groups List
              </div>
              <div className="flex space-x-4">
                <Button
                  onClick={() =>
                    navigate("/group-management/add", {
                      state: { communityId: communityId },
                    })
                  }
                  className="flex items-center px-4 py-2 bg-[#14B8A6] text-sm sm:text-base text-white rounded hover:bg-[#2DD4BF]"
                >
                  <FaPlus className="mr-2" /> Create Group
                </Button>
              </div>
            </div>
            <hr />
            <div className="overflow-x-auto w-full md:max-w-[calc(100vw-375px)]">
              {showLoading ? (
                // {!page.groupsTable.length ? (
                <LoadingImage />
              ) : data.length > 0 ? (
                // ) : page.groupsTable.length ? (
                <DataTable
                  columns={columns}
                  data={data}
                  // data={page.groupsTable}
                  actions={actions}
                />
              ) : (
                <div className="text-center text-gray-500 py-6">
                  No requests found.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activate Modal */}
      <Modal
        isOpen={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        title="Activate Group"
        description="Are you sure you want to Activate this group?"
        icon={<FaExclamationTriangle className="text-green-500" />}
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowActivateModal(false)}
            >
              No, Cancel
            </Button>
            <Button onClick={handleConfirmActivate} disabled={loading}>
              {loading ? "Activating" : "Yes Activate this group"}
            </Button>
          </div>
        }
      ></Modal>

      {/* Deactivate Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Deactivate Group"
        description="Are you sure you want to deactivate this group?"
        icon={<FaExclamationTriangle className="text-red-500" />}
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowDeleteModal(false)}
            >
              No, Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={handleConfirmDeactivate}
              disabled={loading}
            >
              {loading ? "Deactivating" : "Yes Deactivate this group"}
            </Button>
          </div>
        }
      ></Modal>
    </div>
  );
};

export default GroupManagement;
